import { ReactNode } from 'react';
import type { MessageDescriptor } from 'react-intl';
import type { ActivityType } from '@alltrails/types/ActivityType';
import type { FeatureEntitlement } from '@alltrails/types/FeatureEntitlement';
import type { SubscriptionTier } from '@alltrails/types/SubscriptionTier';

// The core map type that is required to render the Mapbox map
export type BaseStyleId =
  | 'adminBasemap'
  | 'alltrailsOutdoorsV2'
  | 'alltrailsOutdoorsV3'
  | 'alltrailsSatelliteV2'
  | 'alltrailsSatelliteMinimal'
  | 'googleSatellite'
  | 'ignPlan'
  | 'ignScan'
  | 'opencycle'
  | 'openstreet'
  | 'osGreatBritain'
  | 'roadmap'
  | 'topo'
  | 'worldparks'
  | 'worldtopo';

// Static styles are basic overlays that don't need API calls to render
export type StaticStyleId = 'adminOverlay' | 'waymarkedCycling' | 'waymarkedHiking' | 'waymarkedMTB';

// Dynamic (a term made up for our needs) styles are overlays that rely on API calls to render
// and the data can be tightly coupled with AllTrails data
export type DynamicStyleId =
  | 'after'
  | 'clusterTrails'
  | 'communityActivity'
  | 'communityWaypoints'
  | 'contribs'
  | 'distanceMarkers'
  | 'fallFoliage'
  | 'geoBoundaries'
  | 'groundConditions'
  | 'heatmap'
  | 'highDefTerrain'
  | 'limit'
  | 'mapsAll'
  | 'nearbyTrails'
  | 'newHeatmap'
  | 'pendingTrails'
  | 'personalHeatmap'
  | 'photoHeatmap'
  | 'photos'
  | 'photospot'
  | 'precipitation'
  | 'privateLands'
  | 'publicLands'
  | 'recordingEnd'
  | 'recordingStart'
  | 'recordingsAll'
  | 'recordingsPop'
  | 'savedRoutePolyline'
  | 'slopeAngle'
  | 'snowDepth'
  | 'surfaceType'
  | 'temperature'
  | 'trailHeatmap'
  | 'userWaypoints'
  | 'waypoints'
  | 'wildfire';
export type ConditionStyleId = Extract<DynamicStyleId, 'groundConditions' | 'precipitation' | 'temperature' | 'snowDepth'>;

export type StyleId = BaseStyleId | StaticStyleId | DynamicStyleId;

export type BaseStyleCardConfig = {
  displayName: MessageDescriptor | string;
  description?: ReactNode;
  imgSrc: string;
  key: BaseStyleId;
  requiresAdmin?: boolean;
  requiresPLP?: boolean;
  requiresAdminStyleSourceUrl?: boolean;
  supports3d?: boolean;
  // Subscription tier indicator rendered inline on the card. Leave undefined
  // for free-for-all-users overlays (e.g. personalHeatmap). Tier is decoupled
  // from `featureEntitlement` on purpose — some Peak-tier layers gate via
  // different entitlement strings, and free layers don't gate at all.
  tier?: SubscriptionTier;
  featureEntitlement?: FeatureEntitlement;
};

export type StaticStyleCardConfig = {
  displayName: MessageDescriptor | string;
  description?: ReactNode;
  imgSrc: string;
  key: StaticStyleId;
  requires2d?: boolean;
  requiresAdmin?: boolean;
  requiresPLP?: boolean;
  requiresAdminStyleSourceUrl?: boolean;
};

export type DynamicStyleCardConfig = {
  displayName: MessageDescriptor | string;
  // When set, the style is auto-enabled the first time it appears in supportedDynamicStyleIds for a given map session.
  defaultEnabled?: boolean;
  description?: ReactNode;
  featureEntitlement?: FeatureEntitlement;
  adminImgSrc?: string;
  imgSrc?: string;
  metricImgSrc?: string;
  key: DynamicStyleId;
  hasHeatmap?: boolean;
  /** Hide the admin badge on this card (the AT logo is intended for basemaps, not overlays). */
  hideAdminBadge?: boolean;
  hideFromPLP?: boolean;
  requires2d?: boolean;
  requiresAdmin?: boolean;
  requiresPLP?: boolean;
  // Subscription tier indicator rendered inline on the card. Leave undefined
  // for free-for-all-users overlays (e.g. personalHeatmap). Tier is decoupled
  // from `featureEntitlement` on purpose — some Peak-tier layers gate via
  // different entitlement strings, and free layers don't gate at all.
  tier?: SubscriptionTier;
  // The minimum zoom level at which the style is visible
  minzoom?: number;
};

export type StyleCardConfig = BaseStyleCardConfig | StaticStyleCardConfig | DynamicStyleCardConfig;

export type SourceConfig = {
  attribution: string;
  maxzoom?: number;
  minzoom?: number;
  tileSize?: number;
};

export type LayerConfig = {
  paint?: {
    opacity?: number;
    'raster-opacity'?: number;
  };
};

export enum BoundaryFilterType {
  alertClosure = 'alert-closure',
  alertNonClosure = 'alert-non-closure',
  osm = 'osm',
  groupPermissions = 'group-permissions'
}

export type AdminStyleSettings = {
  activityType?: ActivityType | 'all';
  styleSourceUrls?: Partial<Record<StyleId, string>>;
  boundaryFilters?: BoundaryFilterType[];
};
